


import {
  ref,
  defineComponent,
  computed,
  onMounted,
  reactive,
  onUnmounted,
  watch,
} from '@vue/composition-api';
import { useStore } from '@/hooks/useStore';
import { useRoute } from '@/hooks/useRoute';
import ExtremeMap from '@/components/lib/ExtremeMap/index.vue';
import PrintPagination from '@/components/lib/PrintPagination/index.vue';
import {
  convReport,
  convSearchParamTsTo,
  loadHokkaidoAirportMtxs,
} from '@/components/ExternalReports/HokkaidoAirport/utils/index';
import { Settings as UserSettings } from '@/models/apis/user/userResponse';
import { ExtremeMapEssentials } from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportMap';
import hokkaidoAirportReportApi from '@/apis/externalReports/hokkaidoAirport/hokkaido_airport_report';
import { waitForUserAndMasters } from '@/lib/masterHelper';
import { dtFormat, dateStrToDate } from '@/lib/dateHelper';
import { setPrintPageSize } from '@/lib/printPageHelper';
import { requestPhotos, revokePhotosObjectURLs } from '@/lib/imageHelper';
import {
  HAP_PHOTO_HEADER_DIVIDING_MARK,
  HAP_PHOTO_NUM_LIMIT_PER_PAGE,
  HAP_CURRENT_PAGE_DEFAULT,
  HAP_ITEMS_PER_PAGE_DEFAULT,
  HAP_ITEMS_PER_PAGE_LIMIT,
} from '@/consts/externalReports/hokkaidoAirport/report_print';
import type {
  HokkaidoAirportReportWithPhotos,
  HokkaidoAirportReportExt,
  HokkaidoAirportReportPhotoExt,
} from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportReport';
import type {
  HokkaidoAirportSettings,
  HokkaidoAirportLabelPc,
} from '@/models/apis/externalReports/hokkaidoAirport/hokkaidoAirportSettings';
import {
  buildTrackVisibilityQuery,
  isHokkaidoAirportExternalReportSettings,
  pairPrintReportsWithMaps,
  parseTrackVisibilityQuery,
} from '@/lib/externalReports/externalReportHelper';
import { useRequestGuard } from '@/composables/useRequestGuard';
import SensorChart, { SensorData } from '@/components/lib/SensorChart.vue';
import {
  showChart,
  processWorkCycles,
} from '@/components/ExternalReports/HokkaidoAirport/ReportPrint/utils';
import { NO_PHOTO_IMAGE, EXTERNAL_REPORT_TYPE } from '@/consts/externalReports/hokkaidoAirport';
import {
  useInspectionPageLayout,
} from '@/components/ExternalReports/HokkaidoAirport/composables/useInspectionPageLayout';
import {
  buildInspectionSheets,
  formatInspectionLegend,
} from '@/lib/externalReports/hokkaidoAirportInspection';
import type { InspectionSheet } from '@/models/externalReports/hokkaidoAirport/hokkaidoAirportInspectionSheet';

const INSPECTION_TS_SEPARATOR = '　';

interface HokkaidoAirportPrintReport extends HokkaidoAirportReportExt {
  reportPhotos: Record<string, HokkaidoAirportReportPhotoExt[]>;
  sensorData: SensorData;
  isMapAvailable: boolean;
  // 点検表のページごとの表。1ページに収まらない表は次のページへ移す
  inspectionPages: InspectionSheet[][];
}
interface PrintHokkaidoAirportReportState {
  reports: HokkaidoAirportReportExt[];
  paginatedReports: HokkaidoAirportPrintReport[];
  extremeMapEssentials: ExtremeMapEssentials | null;
  inputItemsPerPage: string;
  isTrackVisible: boolean;
}

export default defineComponent({
  setup() {
    const state = reactive<PrintHokkaidoAirportReportState>({
      reports: [],
      paginatedReports: [],
      extremeMapEssentials: null,
      inputItemsPerPage: HAP_ITEMS_PER_PAGE_DEFAULT.toString(),
      isTrackVisible: true,
    });
    const shouldShowPagination = computed<boolean>(() => {
      return state.reports.length > itemsPerPage.value;
    });
    const store = useStore();
    const userState = store.state.user;
    const { doRequest, isRequesting } = useRequestGuard((errorMessage) => {
      console.error(errorMessage);
    });
    const userSettings = computed<UserSettings>(() => {
      return userState.settings;
    });
    const hokkaidoAirportUserSettings = ref<HokkaidoAirportSettings | null>(null);
    const pcLabels = computed<HokkaidoAirportLabelPc>(() => {
      return hokkaidoAirportUserSettings.value?.pc ?? {
        title: {},
        button: {},
        field_label: {},
        table_header: {},
      } as HokkaidoAirportLabelPc;
    });
    const { route, router } = useRoute();
    const externalReportId = computed<string>(() => {
      return route.value.params.externalReportId;
    });
    const currentPage = computed<number>(() => {
      const raw = Number(route.value.params.currentPage);
      const isNumeric = Number.isFinite(raw);
      const n = isNumeric ? Math.trunc(raw) : HAP_CURRENT_PAGE_DEFAULT;
      return n >= HAP_CURRENT_PAGE_DEFAULT ? n : HAP_CURRENT_PAGE_DEFAULT;
    });
    const itemsPerPage = computed<number>(() => {
      const raw = Number(route.value.params.itemsPerPage);
      const n = Number.isFinite(raw) ? Math.trunc(raw) : NaN;
      if (!Number.isFinite(n) || n < HAP_CURRENT_PAGE_DEFAULT) return HAP_ITEMS_PER_PAGE_DEFAULT;
      // 1ページあたりの表示件数が上限を超えている場合は上限値を設定
      return Math.min(n, HAP_ITEMS_PER_PAGE_LIMIT);
    });

    const reportDtFormat = (dt: Date | null) => {
      return dtFormat(dt, 'yyyy年m月d日 HH時MM分');
    };
    // 最初は全ての表を1ページに置き、収まらなければ描画後に次のページへ移す
    const buildPrintInspectionPages = (report: HokkaidoAirportReportExt): InspectionSheet[][] => {
      if (!hokkaidoAirportUserSettings.value) return [];

      const sheets = buildInspectionSheets(report, hokkaidoAirportUserSettings.value.inspection);
      return sheets.length > 0 ? [sheets] : [];
    };
    const formatInspectionCompanyLine = (report: HokkaidoAirportPrintReport): string => {
      const labels = pcLabels.value.table_header;
      const companyName = hokkaidoAirportUserSettings.value?.external_report_type_name ?? '';
      return `${companyName}（${labels.airport_name}：${report.airportName}、${labels.han_name}：${report.hanName}）`;
    };
    const formatInspectionTsLine = (report: HokkaidoAirportPrintReport): string => {
      const labels = pcLabels.value.table_header;
      const preWorkTs = `${labels.pre_work_inspection_ts}：${reportDtFormat(report.preWorkInspectionTs)}`;
      const postWorkTs = `${labels.post_work_inspection_ts}：${reportDtFormat(report.postWorkInspectionTs)}`;
      return [preWorkTs, postWorkTs].join(INSPECTION_TS_SEPARATOR);
    };
    const inspectionLegend = computed<string>(() => {
      return formatInspectionLegend(hokkaidoAirportUserSettings.value?.inspection.symbols ?? []);
    });
    const extremeMap = ref<InstanceType<typeof ExtremeMap>[]>([]);
    const { inspectionCommentContainers, inspectionPageElements } = useInspectionPageLayout(
      () => state.paginatedReports,
    );
    const convPrintReport = async(reports: HokkaidoAirportReportExt[]): Promise<HokkaidoAirportPrintReport[]> => {
      await loadHokkaidoAirportMtxs(reports);
      const resultPromises = reports.map(async(report) => {
        await requestPhotos(report.photosExt);
        const sensorData = showChart(report);
        return {
          ...report,
          reportPhotos: convReportPhotos(report.photosExt),
          isMapAvailable: false,
          sensorData,
          inspectionPages: buildPrintInspectionPages(report),
        };
      });
      return Promise.all(resultPromises);
    };
    const convReportPhotos = (photos: HokkaidoAirportReportPhotoExt[]): Record<string, HokkaidoAirportReportPhotoExt[]> => {
      const reportPhotos: Record<string, HokkaidoAirportReportPhotoExt[]> = {};
      if (!hokkaidoAirportUserSettings.value) return reportPhotos;

      hokkaidoAirportUserSettings.value.choices.photo_types.forEach(photoType => {
        const reportTypePhotos = photos.filter(photo => photo.photoType === photoType);
        if (reportTypePhotos.length === 0) return;

        for (let i = 0; i < Math.ceil(reportTypePhotos.length / HAP_PHOTO_NUM_LIMIT_PER_PAGE); i++) {
          const start = i * HAP_PHOTO_NUM_LIMIT_PER_PAGE;
          const end = (i + 1) * HAP_PHOTO_NUM_LIMIT_PER_PAGE;
          const key = i + HAP_PHOTO_HEADER_DIVIDING_MARK + photoType;
          reportPhotos[key] = reportTypePhotos.slice(start, end);
        }
      });
      return reportPhotos;
    };
    const getPhotoType = (photoHeaderInfo: string): string => {
      return photoHeaderInfo.split(HAP_PHOTO_HEADER_DIVIDING_MARK)[1];
    };
    const calculationWaitingTime = (report: HokkaidoAirportReportExt): string => {
      const workCycleMetrics = processWorkCycles(report);
      return workCycleMetrics.standbyTimeTotal;
    };
    const searchReport = async(): Promise<HokkaidoAirportReportWithPhotos[]> => {
      if (externalReportId.value) {
        const report = await hokkaidoAirportReportApi.get(Number(externalReportId.value));
        return [report];
      }
      const hanName = route.value.params.hanName;
      const searchParam = {
        ts_from: dateStrToDate(route.value.params.dateFrom) || new Date(),
        ts_to: convSearchParamTsTo(dateStrToDate(route.value.params.dateTo) || new Date()),
        han_name: hanName === 'all' ? '' : hanName,
      };
      const reports = await hokkaidoAirportReportApi.index(searchParam);
      return reports;
    };
    watch(() => extremeMap.value, () => {
      if (!extremeMap.value) {
        return;
      }
      showHokkaidoAirportReport();
    });
    const drawReportLayer = (
      map: InstanceType<typeof ExtremeMap>,
      report: HokkaidoAirportPrintReport,
      fitToExtent: boolean,
    ): void => {
      if (!hokkaidoAirportUserSettings.value) return;

      map.showExternalReportLayer(report, {
        photoTypeVisibility: hokkaidoAirportUserSettings.value.choices.photo_types.reduce((acc, type) => {
          acc[type] = true;
          return acc;
        }, {} as Record<string, boolean>),
        fitToExtent,
        externalReportUserSettings: hokkaidoAirportUserSettings.value,
        showTrack: state.isTrackVisible,
      });
    };
    const showHokkaidoAirportReport = () => {
      const h = Math.max(240, window.innerHeight - 300);
      if (!hokkaidoAirportUserSettings.value) return;

      for (const { report, map } of pairPrintReportsWithMaps(state.paginatedReports, extremeMap.value)) {
        // extremeMap.valueでwatchすると、地図を表示し終わったものも含まれるため、
        // 地図を表示し終わったものは処理せず再描画されるのを防ぐ
        if (report.isMapAvailable) {
          continue;
        }
        map.setMapHeight(h);
        map.triggerResize();
        map.removeExternalReportLayer();
        drawReportLayer(map, report, true);
        report.isMapAvailable = true;
      }
    };
    // 表示範囲は変えずにレイヤーだけ描き直す
    const onChangeTrackVisibility = (): void => {
      for (const { report, map } of pairPrintReportsWithMaps(state.paginatedReports, extremeMap.value)) {
        // 未描画の地図は showHokkaidoAirportReport が state.isTrackVisible で描くため、ここでは触らない
        if (!report.isMapAvailable) {
          continue;
        }
        drawReportLayer(map, report, false);
      }
    };
    const paginatePrintReport = async() => {
      const nextPageIdx = currentPage.value * itemsPerPage.value;
      const minIdx = nextPageIdx - itemsPerPage.value;
      const reports = state.reports.slice(minIdx, nextPageIdx);
      await doRequest(async() => {
        state.paginatedReports = await convPrintReport(reports);
      }, '報告書の取得に失敗しました。');
    };
    const moveToSelectedPage = (currentPage: number, itemsPerPage: number) => {
      // ページ切り替え時に地図が表示されなくなる問題を回避するため、リダイレクトしてページングする
      const routeObj = {
        name: 'PrintExternalHokkaidoAirportReportAll',
        params: {
          dateFrom: route.value.params.dateFrom,
          dateTo: route.value.params.dateTo,
          hanName: route.value.params.hanName,
          currentPage: currentPage.toString(),
          itemsPerPage: itemsPerPage.toString(),
        },
        query: buildTrackVisibilityQuery(state.isTrackVisible),
      };
      const obj = router.resolve(routeObj);
      window.location.href = obj.href;
    };
    const onClickPageChange = (pageNum: number) => {
      moveToSelectedPage(pageNum, itemsPerPage.value);
    };
    const onClickItemsPerPageChange = (itemsPerPage: number) => {
      moveToSelectedPage(HAP_CURRENT_PAGE_DEFAULT, itemsPerPage);
    };
    const updatePhotoComment = async(reportId: number, photo: HokkaidoAirportReportPhotoExt) => {
      await doRequest(async() => {
        await hokkaidoAirportReportApi.updatePhotoComment(reportId, photo.id, photo.comment);
      }, '写真のコメント更新に失敗しました。');
    };
    onMounted(async() => {
      document.body.classList.add('A4');
      setPrintPageSize('A4 portrait');
      state.isTrackVisible = parseTrackVisibilityQuery(route.value.query);

      await doRequest(async() => {
        await waitForUserAndMasters();
        const reports = await searchReport();

        state.reports = reports.map(e =>
          convReport(e),
        );
      }, '初期化に失敗しました。');
      const externalReportSetting = window.master.externalReportSettings?.find(
        (setting) => setting.external_report_type === EXTERNAL_REPORT_TYPE,
      );
      if (!externalReportSetting) return;

      hokkaidoAirportUserSettings.value = isHokkaidoAirportExternalReportSettings(externalReportSetting)
        ? externalReportSetting
        : null;

      state.extremeMapEssentials = {
        userSettings: userSettings.value,
        kpMap: window.master.kpMap,
        externalReportSettings: hokkaidoAirportUserSettings.value,
      };
      state.inputItemsPerPage = itemsPerPage.value.toString();
      await paginatePrintReport();
    });

    onUnmounted(() => {
      document.body.classList.remove('A4');
      state.reports.map(report => {
        if (!report.photosExt) return;
        revokePhotosObjectURLs(report.photosExt.map(photo => photo.savedImage));
      });
    });

    return {
      state,
      // ref
      extremeMap,
      inspectionCommentContainers,
      inspectionPageElements,
      // computed
      hokkaidoAirportUserSettings,
      shouldShowPagination,
      currentPage,
      itemsPerPage,
      isRequesting,
      pcLabels,
      NO_PHOTO_IMAGE,
      calculationWaitingTime,
      reportDtFormat,
      dtFormat,
      getPhotoType,
      onClickPageChange,
      onClickItemsPerPageChange,
      updatePhotoComment,
      formatInspectionCompanyLine,
      formatInspectionTsLine,
      inspectionLegend,
      onChangeTrackVisibility,
    };
  },
  components: {
    PrintPagination,
    SensorChart,
  },
});
